import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { summariseSweeps, type SweepStore } from "../lib/sweepHealth";
import { formatKr, stateTextClass } from "../ui";
import { useWs } from "../ws";

type SpendRule = {
	ruleId: number;
	label: string;
	enabled: boolean;
	autoBuy: boolean;
	maxDailyMinor: number | null;
	spentMinor: number;
};

type Metrics = {
	uptimeSec: number;
	events1h: Record<string, number>;
	activeWatches: number;
	recentBuys: Record<string, number>;
	breaker: Record<string, { open: boolean; openUntil: number }>;
	lastSweep: Record<string, number | null>;
	dbBytes: number;
	bootedAt: number;
	// Only present on /api/dashboard, not the plainer /api/metrics — this
	// strip fetches the dashboard endpoint specifically so the health line
	// can carry spend-against-cap alongside sweep age and breaker state, per
	// the Now spec. The rest of this component ignores dashboard's other
	// extra fields (events24h, buys24h, stores, recentRestocks).
	spendToday: SpendRule[];
	// Per-store sweep timings, cadences and breaker state. Read in place of the
	// flat `lastSweep` map: that carried ids and ages and nothing to judge them
	// against, which is why the old line could only list rather than report.
	stores: SweepStore[];
};

const REFRESH_MS = 30_000;

function fmtBytes(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
	return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtAgo(ts: number | null): string {
	if (ts == null) return "never";
	const d = Date.now() - ts;
	if (d < 0) return "now";
	if (d < 60_000) return `${Math.max(1, Math.floor(d / 1000))}s ago`;
	if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
	return `${Math.floor(d / 3_600_000)}h ago`;
}

function fmtUptime(sec: number): string {
	if (sec < 60) return `${sec}s`;
	if (sec < 3600) return `${Math.floor(sec / 60)}m`;
	if (sec < 86_400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
	return `${Math.floor(sec / 86_400)}d`;
}

export function MetricsStrip() {
	const [m, setM] = useState<Metrics | null>(null);

	const load = useCallback(
		() =>
			fetch("/api/dashboard")
				.then((r) => r.json())
				.then((data) => {
					setM(data as Metrics);
				})
				.catch(() => {}),
		[],
	);

	useEffect(() => {
		load();
		const t = setInterval(load, REFRESH_MS);
		return () => clearInterval(t);
	}, [load]);

	useWs((msg) => {
		if (msg.type === "sweep.complete") {
			load();
		}
	});

	if (!m) {
		return <div className="h-40 rounded-[10px] border border-hairline bg-paper" />;
	}

	const eventsTotal = Object.values(m.events1h).reduce((a, b) => a + b, 0);
	const recentBuysTotal = Object.values(m.recentBuys).reduce((a, b) => a + b, 0);
	const spend = spendStat(m.spendToday);
	const sweeps = summariseSweeps(m.stores ?? []);

	return (
		<div className="overflow-hidden rounded-[10px] border border-hairline bg-paper">
			<dl className="grid grid-cols-3 gap-px bg-hairline sm:grid-cols-6 lg:grid-cols-3">
				<Stat label="uptime" value={fmtUptime(m.uptimeSec)} />
				<Stat label="watches" value={String(m.activeWatches)} />
				<Stat label="events/h" value={String(eventsTotal)} title={breakdown(m.events1h)} />
				<Stat label="buys" value={String(recentBuysTotal)} title={breakdown(m.recentBuys)} />
				<Stat label="spend" value={spend.value} valueClassName={spend.valueClassName} title={spend.title} />
				<Stat label="database" value={fmtBytes(m.dbBytes)} />
			</dl>

			<SweepLine health={sweeps} />
		</div>
	);
}

/**
 * Sweep freshness as a sentence, with the per-store table behind a
 * disclosure.
 *
 * This replaced a line that joined every store's age with middle dots and let
 * the row truncate: it was unreadable at five stores, worse at nine, and the
 * store it cut off was as likely as any to be the broken one. Nothing here
 * grows with the number of stores.
 */
function SweepLine({ health }: { health: ReturnType<typeof summariseSweeps> }) {
	// Open from the start where the health block has a column of its own to
	// grow into (lg, beside the stream); folded on a phone, where it would push
	// the stream a screen down.
	const [open, setOpen] = useState(
		() => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
	);
	const tone = health.state === "ok" ? "text-ink" : stateTextClass(health.state);

	return (
		<div className="border-t border-hairline">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				className="flex min-h-tap w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-raised"
			>
				<span className={`flex-1 text-xs font-medium ${tone}`}>{health.summary}</span>
				<span className="text-[11px] text-muted">{open ? "Hide stores" : "Per store"}</span>
				<ChevronDown
					className={`size-3.5 shrink-0 text-muted transition-transform ${open ? "rotate-180" : ""}`}
					aria-hidden
				/>
			</button>

			{open && (
				<ul className="border-t border-hairline px-3 py-1.5">
					{health.rows.map((r) => (
						<li key={r.id} className="flex h-6 items-center gap-3 text-xs">
							<span className={`flex-1 truncate ${r.state === "off" ? "text-muted" : "text-ink"}`}>{r.name}</span>
							<span
								className={`text-[11px] ${
									r.state === "ok" || r.state === "off" ? "text-muted" : stateTextClass(r.state)
								}`}
							>
								{r.ago}
							</span>
							{(r.state === "needs" || r.state === "fault") && (
								<span className={`text-[11px] ${stateTextClass(r.state)}`}>
									{r.state === "fault" ? "unreachable" : "overdue"}
								</span>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

function breakdown(counts: Record<string, number>): string {
	return (
		Object.entries(counts)
			.map(([k, v]) => `${k}=${v}`)
			.join(" · ") || "no recent activity"
	);
}

/**
 * Condenses however many autobuy rules carry a daily cap into the one
 * number the health line has room for: the rule closest to its cap, since
 * that's the one that needs attention if any does. Below 70% this is the
 * ordinary case — neutral, no word. At 70%+ it's `needs`, at 100%+ `fault`;
 * either way the word travels with the colour (state is never colour
 * alone), and the full per-rule breakdown is one hover away in `title`,
 * matching how `events/1h` and `recent buys` expose their breakdown.
 */
function spendStat(spendToday: SpendRule[]): {
	value: string;
	valueClassName?: string;
	title?: string;
} {
	const capped = spendToday.filter((r) => r.maxDailyMinor != null && r.maxDailyMinor > 0);
	if (capped.length === 0) {
		return { value: "No caps" };
	}
	const withPct = capped.map((r) => ({
		...r,
		pct: Math.min(100, Math.round((r.spentMinor / (r.maxDailyMinor as number)) * 100)),
	}));
	const worst = withPct.reduce((a, b) => (b.pct > a.pct ? b : a));
	const band: "fault" | "needs" | null = worst.pct >= 100 ? "fault" : worst.pct >= 70 ? "needs" : null;
	// "near cap" (not "approaching cap") because the ≥70% band's value cell is
	// only ~98px wide at 375px and the longer word clips mid-word — measured:
	// "72% · approaching cap" bold is ~132px, "72% · near cap" fits.
	const word = band === "fault" ? "at cap" : band === "needs" ? "near cap" : null;
	const title = withPct
		.map((r) => `${r.label} ${r.pct}% (${formatKr(r.spentMinor)}/${formatKr(r.maxDailyMinor)})`)
		.join(" · ");
	return {
		value: word ? `${worst.pct}% · ${word}` : `${worst.pct}%`,
		valueClassName: band ? `${stateTextClass(band)} font-bold` : undefined,
		title,
	};
}

/**
 * One figure with its name beneath it. Value first and heavier so six of them
 * scan as six things rather than as one run-on sentence, which is how the old
 * inline `label value` pairs read. Figures are tabular so the grid does not
 * shift as they tick. dt comes first in the DOM for screen readers and is
 * flipped below the value visually.
 */
function Stat({
	label,
	value,
	title,
	valueClassName = "text-ink",
}: {
	label: string;
	value: string;
	title?: string;
	valueClassName?: string;
}) {
	return (
		<div title={title} className="flex min-w-0 flex-col-reverse bg-paper px-3 py-2">
			<dt className="mt-1 text-[11px] leading-none text-muted">{label}</dt>
			<dd className={`truncate text-[17px] leading-none font-semibold font-tag ${valueClassName}`}>{value}</dd>
		</div>
	);
}
